import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { apiGetProjectSourceTree } from './api';
import { FileTreeView, countNodes } from './FileTree';
import PageLoader from './PageLoader';

export default function ProjectFilesPage() {
  const { project } = useAuth();
  const navigate = useNavigate();

  const [tree,         setTree]         = useState(null);
  const [resourcePath, setResourcePath] = useState('');
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');

    apiGetProjectSourceTree(project.id)
      .then(data => {
        setResourcePath(data?.resourcePath ?? '');
        setTree(data?.tree ?? null);
      })
      .catch(() => setError('Failed to load project files.'))
      .finally(() => setLoading(false));
  }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const children   = tree?.children ?? [];
  const totalItems = children.reduce((s, n) => s + countNodes(n), 0);

  return (
    <div style={S.page}>

      {/* ── Header ─────────────────────────────────── */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          <span style={S.headerIcon}>📂</span>
          <div>
            <div style={S.headerTitle}>{project.name} — Source Files</div>
            <div style={S.headerPath}>
              <span style={S.pathMono}>
                {resourcePath || `$Server\\${project.customerId ?? '—'}\\${project.id}\\Source`}
              </span>
              {!loading && (
                <span style={S.itemBadge}>{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
              )}
            </div>
          </div>
        </div>
        <button
          className="btn btn-primary"
          style={{ fontSize: 13, padding: '7px 16px' }}
          onClick={() => navigate('../upload')}
        >
          ⬆️ Upload ZIP
        </button>
      </div>

      {/* ── Tree area ──────────────────────────────── */}
      <div style={S.body}>
        {loading && <PageLoader label="Loading files…" />}

        {!loading && error && (
          <div className="error-alert">{error}</div>
        )}

        {!loading && !error && (
          <div style={S.treeCard}>
            <FileTreeView node={tree} />
          </div>
        )}
      </div>

    </div>
  );
}

const S = {
  page: {
    display: 'flex', flexDirection: 'column',
    flex: 1, minHeight: 0, height: '100%', overflow: 'hidden',
  },

  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 20px',
    borderBottom: '1px solid var(--border)',
    background: 'var(--bg-secondary, rgba(0,0,0,0.02))',
    flexShrink: 0,
  },
  headerLeft:  { display: 'flex', alignItems: 'center', gap: 12 },
  headerIcon:  { fontSize: 22, flexShrink: 0 },
  headerTitle: { fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 },
  headerPath:  { display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, flexWrap: 'wrap' },
  pathMono:    { fontFamily: 'var(--font-mono, monospace)', fontSize: 11, color: 'var(--text-muted)' },
  itemBadge: {
    background: 'var(--info-bg, rgba(21,101,192,0.1))',
    border: '1px solid rgba(21,101,192,0.2)',
    borderRadius: 10, color: 'var(--primary)',
    fontSize: 10, fontWeight: 700, padding: '1px 8px',
  },

  body: { flex: 1, overflowY: 'auto', padding: '16px 20px' },
  treeCard: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 8, padding: '14px 16px',
    boxShadow: 'var(--shadow-xs)',
  },
};
